import { useEffect, useMemo, useReducer, useState } from 'react';
import { Container } from '@mui/material';
import {
  ISaveRequestOutput,
  getRequestIds,
  useAlertContext,
  useCancelRequests,
  useDocumentTitle,
} from '../common';
import { initRequestsState, requestsReducer, useGetRequests } from '.';
import { RequestStatus } from '../request-status';
import { getRequestsCallStatus } from './requests';
import config from '../config.json';
import { noop, orderBy as lorderBy } from 'lodash/fp';
import { flow } from 'fp-ts/function';

export function RequestStatusEndUser() {
  useDocumentTitle({ title: 'Status of Requests' });

  const [requestState, dispatchRequest] = useReducer(
    requestsReducer,
    initRequestsState
  );

  const { reloadRequests } = useGetRequests({ requestState, dispatchRequest });

  const apiCallStatus = getRequestsCallStatus(requestState);

  const toggleRequestSelection = (payload: ISaveRequestOutput) =>
    dispatchRequest({ type: 'ToggleRequestSelection', payload });

  const clearRequestSelection = () =>
    dispatchRequest({ type: 'ClearRequestSelection', payload: void 0 });

  const [now] = useState(new Date());

  const selectedRequestIds = useMemo(
    () => getRequestIds(requestState.selectedRequests),
    [requestState.selectedRequests]
  );
  const { cancelRequestsStatus, cancelRequests, resetCancelRequests } =
    useCancelRequests({
      requestIds: selectedRequestIds,
    });

  const { setAlert } = useAlertContext();

  const confirmCancelRequests = () =>
    setAlert(a => ({
      ...a,
      title: 'CSOS',
      message: () => <>{config.requestCancelConfirm}</>,
      okAction: true,
      cancelAction: true,
      isOpen: true,
      isOk: false,
      onOk: cancelRequests,
    }));

  useEffect(() => {
    const clearAlert = () =>
      setAlert(a => ({
        ...a,
        isOk: false,
        isOpen: false,
      }));

    cancelRequestsStatus === null &&
      setAlert(a => ({
        ...a,
        title: 'CSOS Canceling Requests',
        message: () => <>{config.requestCanceling}</>,
        okAction: false,
        cancelAction: false,
        isOpen: true,
        onOk: noop,
      }));

    cancelRequestsStatus === false &&
      setAlert(a => ({
        ...a,
        title: 'CSOS Failure',
        message: () => <>{config.requestCancelFailure}</>,
        okAction: true,
        cancelAction: false,
        isOpen: true,
        onOk: flow(clearAlert, resetCancelRequests),
      }));

    cancelRequestsStatus === true &&
      flow(
        () => {
          const newAlert = {
            title: 'CSOS Success',
            message: () => <>{config.requestCancelSuccess}</>,
            okAction: true,
            cancelAction: false,
            isOpen: true,
            onOk: flow(clearAlert, resetCancelRequests),
          };
          setAlert(a => ({
            ...a,
            ...newAlert,
          }));
        },
        reloadRequests,
        clearRequestSelection
      )();
  }, [cancelRequestsStatus, reloadRequests, setAlert, resetCancelRequests]);

  switch (apiCallStatus) {
    case null:
      return <Container maxWidth="lg">Loading...</Container>;

    case false:
      return <Container maxWidth="lg">Error</Container>;

    case true:
      return (
        <>
          <RequestStatus
            requestStatusType="SelfRequestStatus"
            requests={lorderBy<ISaveRequestOutput>(
              ['updatedAt'],
              ['desc']
            )(requestState.requests)}
            now={now}
            selectedRequests={requestState.selectedRequests}
            toggleRequestSelection={toggleRequestSelection}
            confirmCancelRequests={confirmCancelRequests}
          />
        </>
      );
    case void 0:
      return null;

    default:
      return <Container maxWidth="lg">Idk</Container>;
  }
}
